import React from 'react';
import { useLevelsStore } from '../../store/levelsStore';
import { useFlowSignalsStore, fmtFlowNumber } from '../../store/flowSignalsStore';
import { useFlowLinesStore, FLOW_LINES } from '../../store/flowLinesStore';
import { normalizeToBase } from '../../utils/symbolUtils';
import { Switch } from './SignalsTab';
import type { TradeFinderTabProps } from './tabs';

/**
 * Order-flow line panes under the chart, one switch per line (levelsStore
 * `flowLine*` keys, all off by default). Lines sharing a pane are listed
 * together; the chart adds and removes the panes as the switches change.
 */
const LinesTab: React.FC<TradeFinderTabProps> = ({ symbol }) => {
  const visibility = useLevelsStore((s) => s.visibility);
  const colors = useLevelsStore((s) => s.colors);
  const setLevelVisible = useLevelsStore((s) => s.setLevelVisible);
  const offline = useFlowSignalsStore((f) => f.status?.connected === false);
  const root = normalizeToBase(symbol);
  const lines = useFlowLinesStore((s) => s.byRoot[root]);

  return (
    <div className="tradefinder-lines" data-testid="tradefinder-lines-tab">
      {offline && (
        <div className="levels-note tradefinder-offline" data-testid="tradefinder-offline">flow-signals offline</div>
      )}
      <div className="tradefinder-lines-note">
        Panes under the price: MVI + stop volume share one (zero line), book imbalance has its own, liquidity + price dynamics share one.
      </div>
      {FLOW_LINES.map((l, i) => {
        const pts = lines?.[l.name];
        const last = pts && pts.length > 0 ? pts[pts.length - 1][1] : null;
        const on = !!visibility[l.key];
        return (
          <React.Fragment key={l.key}>
            {(i === 0 || FLOW_LINES[i - 1].group !== l.group) && (
              <div className="levels-group-heading">
                {l.group === 'volume' ? 'Volume pane' : l.group === 'book' ? 'Book pane' : 'Liquidity pane'}
              </div>
            )}
            <Switch
              on={on}
              label={l.label}
              description={l.description}
              swatch={colors[l.key]}
              meta={on && last !== null ? fmtFlowNumber(last) : undefined}
              testId={`tradefinder-toggle-${l.key}`}
              onChange={(next) => setLevelVisible(l.key, next)}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default LinesTab;
